import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Box, Button, CircularProgress, Container, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import appealableTemporaryOrdersImage from '../assets/images/appealable-temporary-orders.png';
import familyRightsHeroImage from '../assets/images/family-rights-due-process-act.png';
import litigantAccessEconomyActImage from '../assets/images/litigant-access-economy-act.png';
import bestInterestChildImage from '../assets/images/best-interests-child-courtroom-1000.png';
import Breadcrumbs from '../components/Breadcrumbs';
import BillStateDirectory from '../components/BillStateDirectory';
import BillStatusStepper from '../components/BillStatusStepper';
// DEPRECATED: Bill text section removed — see docs/BILL_TEXT_FEATURE.md
// import BillText from '../components/BillText';
import NotFound from './NotFound';
import SEO from '../components/SEO';
import ShareButton from '../components/ShareButton';
import StateSelector from '../components/StateSelector';
import WhyBillMatters from '../components/WhyBillMatters';
import { useGeoLocation } from '../contexts/GeoLocationContext';
import { useBillConfig, useBillUrlInfo } from '../hooks/useBillData';
import { trackLegislatorPackClick, trackBillView, trackOfficialBillLinkClick } from '../components/GoogleAnalytics';
import { stateNameToSlug } from '../utils/stateUtils';
import { useBillUrlSync } from '../hooks/useBillUrlSync';
import { useLocationPrompt } from '../hooks/useLocationPrompt';
import { useFloatingButton } from '../hooks/useFloatingButton';
import { useBillDetailSEO } from '../hooks/useBillDetailSEO';
import BillLocationPromptDialog from '../components/BillLocationPromptDialog';

// Hero image mapping using public folder URLs
const heroImageMap: Record<string, string> = {
  'family-first-federal-reform': '/images/family-first-federal-reform.png',
  'a-new-start-at-equality': '/images/a-new-start-at-equality.png',
  'appealable-temporary-orders': appealableTemporaryOrdersImage,
  'cameras-in-courtrooms': '/images/cameras-in-courtrooms.png',
  'child-support-dual-calculation-reform': '/images/child-support-dual-calculation-reform.png',
  'child-support-integrity-act': '/images/child-support-integrity-act.png',
  'justice-delayed-is-justice-denied': '/images/justice-delayed-is-justice-denied.png',
  'litigant-access-economy-act': litigantAccessEconomyActImage,
  'three-strikes': '/images/three-strikes.png',
  'time-taken-time-back': '/images/time-taken-time-back.png',
  'reimbursement-fees-parental-alienation': '/images/reimbursement-fees-parental-alienation.png',
  'family-rights-due-process-act': familyRightsHeroImage,
  'best-interest-of-the-child-definition-act': bestInterestChildImage,
  'parent-child-restoration-act': '/images/parent-child-restoration-act.png',
  'judicial-accountability-referral-act': '/images/judicial-accountability-referral-act.png',
  _default: '/images/family-first-federal-reform.png', // fallback image
};

const BillDetailPage: React.FC = () => {
  const {
    state,
    billSlug: billSlugParam,
    country: countryParam,
    param,
  } = useParams<{
    state?: string;
    billSlug?: string;
    param?: string;
    country?: string;
  }>();
  const { location, setLocation } = useGeoLocation(); // Get user's geolocation
  const navigate = useNavigate();

  // Determine country: URL param → location context → default
  const country = countryParam || location?.country || 'united-states';

  // When BillRouter routes here, billSlug comes from :param
  const billSlug = billSlugParam || param;

  // React Query hooks - state-reactive data fetching
  // Pass the original state slug to useBillUrlInfo so it can be properly converted
  // Use isPending to properly handle disabled queries and prevent "Bill Not Found" flash
  const { data: billConfig, isPending: billConfigPending, error: billConfigError } = useBillConfig(billSlug);

  const {
    data: billUrlInfo,
    isLoading: billUrlLoading,
    error: billUrlError,
  } = useBillUrlInfo(
    billConfig?.pageTitle,
    state, // Use original state slug instead of incorrectly capitalized version
    billSlug,
    country,
  ); // Local state for UI
  const [heroImage, setHeroImage] = useState<string | null>(null);

  // Computed loading and error states
  // Use isPending to account for disabled queries (waiting for parent data)
  // This prevents the "Bill Not Found" flash during initial hydration
  const loading = billConfigPending || billUrlLoading;
  const error = billConfigError ? 'Failed to load bill information' : null;

  // Extract bill URL info with defaults
  const googleDriveUrl = billUrlInfo?.googleDriveUrl || '';
  const billUrl = billUrlInfo?.billUrl || '';

  // Build a direct download URL using drive.google.com/uc — works without Google sign-in
  // docs.google.com/export requires authentication even for publicly shared files
  const googleDriveExportPdfUrl = (() => {
    if (!googleDriveUrl) return null;
    const match = googleDriveUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (!match) return null;
    return `https://drive.google.com/uc?export=download&id=${match[1]}`;
  })();

  const { showLocationPrompt, handleDismissPrompt } = useLocationPrompt(state, location);
  useBillUrlSync({ state, location, setLocation, billSlug, country, navigate });
  const { showFloatingButton, observerInitialized, heroLegislatorButtonRef } = useFloatingButton(googleDriveUrl);

  // Scroll to top when state parameter changes (e.g., when user selects a state)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [state]);

  // Load hero image when billSlug changes
  useEffect(() => {
    if (billSlug) {
      const image = heroImageMap[billSlug] || heroImageMap['_default'];
      setHeroImage(image);
    }
  }, [billSlug]);

  // Track bill page view when bill config resolves
  useEffect(() => {
    if (billConfig?.pageTitle) {
      trackBillView(billConfig.pageTitle, state);
    }
  }, [billConfig?.pageTitle, state]);

  // Helper functions for state name/slug (defined before hooks)
  const getCurrentStateName = React.useCallback(() => {
    // The URL state defines page identity and must not vary by visitor geolocation.
    if (state) {
      return state.charAt(0).toUpperCase() + state.slice(1).replace(/-/g, ' ');
    }
    if (location?.state) {
      return location.state;
    }
    return null;
  }, [location?.state, state]);

  const getCurrentStateSlug = React.useCallback(() => {
    // Keep route metadata stable; geolocation is only a directory-page convenience.
    if (state) return state;
    return location?.state ? stateNameToSlug(location.state) : null;
  }, [location?.state, state]);

  const isStateMode = !!state; // if URL has a state param

  const heroImageUrl = heroImage || heroImageMap[billSlug!] || heroImageMap['_default'];
  const { breadcrumbs, articleSchema, canonicalPath } = useBillDetailSEO({
    isStateMode,
    billConfig,
    billSlug,
    stateName: isStateMode ? getCurrentStateName() : null,
    stateSlug: getCurrentStateSlug(),
    country,
    heroImageUrl,
  });

  if (loading) {
    return (
      <Container component="main">
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1">Loading bill information...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || !billConfig) {
    return <NotFound />;
  }

  return (
    <>
      <SEO
        title={isStateMode ? `${billConfig.pageTitle} | ${getCurrentStateName()} Bill` : billConfig.seoTitle}
        description={
          isStateMode
            ? `Learn how the ${billConfig.pageTitle} would reform family court in ${getCurrentStateName() || 'your state'}. View bill status, legislative materials, and advocacy resources.`
            : `Explore the ${billConfig.pageTitle}, its family court reforms, state-by-state status, legislative materials, and ways to support the proposal today.`
        }
        path={canonicalPath}
        image={heroImageUrl.startsWith('http') ? heroImageUrl : `https://robertgarza.us${heroImageUrl}`}
        schema={articleSchema}
      />
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        {isStateMode && breadcrumbs.length > 0 && (
          <Box
            sx={{
              pt: 2,
              pb: 1,
              px: { xs: 2, md: 4 },
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'center' },
            }}
          >
            <Breadcrumbs items={breadcrumbs} />
          </Box>
        )}
        <Container component="main">
          <Box
            key={heroImage}
            sx={{
              height: '80vh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              padding: { xs: 0, md: 20 },
              position: 'relative',
              zIndex: 1,
              backgroundImage: { xs: heroImage ? `url(${heroImage})` : `url(${heroImageMap['_default']})`, md: 'none' },
              backgroundPosition: { xs: 'top' },
              backgroundSize: { xs: 'contain' },
              backgroundRepeat: { xs: 'no-repeat' },
              '&::before': {
                content: '""',
                display: { xs: 'block', md: 'none' },
                position: 'absolute',
                inset: 0,
                bgcolor: 'background.paper',
                opacity: 0.7,
                pointerEvents: 'none',
                zIndex: 0,
              },
              '& > *': {
                position: 'relative',
                zIndex: 1,
              },
            }}
          >
            {isStateMode && (
              <Box sx={{ mb: 3 }}>
                <StateSelector
                  asLink={true}
                  highlightLocation
                  billTitle={billConfig.pageTitle}
                  country={country}
                  stateName={getCurrentStateName() ?? undefined}
                />
              </Box>
            )}
            {!isStateMode && (
              <Box sx={{ mb: 3 }}>
                <StateSelector
                  asLink={true}
                  highlightLocation
                  billTitle={billConfig.pageTitle}
                  linkText={location?.state ? `${location.state} Bill Status` : 'Set Location for Bill Status'}
                  country={country}
                />
              </Box>
            )}
            {isStateMode && (
              <Box sx={{ mb: 4, width: '100%', maxWidth: 800 }}>
                <BillStatusStepper billTitle={billConfig.pageTitle} country={country} />
              </Box>
            )}
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: '800',
                mb: 1,
                fontSize: { xs: '2rem', md: '3.5rem' },
              }}
            >
              {billConfig.pageTitle}
            </Typography>
            {billConfig.secondaryTitle && (
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: '600',
                  fontSize: { xs: '1.25rem', md: '2rem' },
                  color: 'primary.main',
                }}
              >
                {billConfig.secondaryTitle}
              </Typography>
            )}
            {billConfig.shortDescription && (
              <Typography
                variant="h6"
                component="p"
                sx={{
                  mb: 4,
                  maxWidth: '800px',
                  lineHeight: 1.6,
                  color: 'text.secondary',
                }}
              >
                {billConfig.shortDescription}
              </Typography>
            )}
            {isStateMode ? (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 4 }}>
                {/* View Legislator Pack button - shown when googleDriveUrl exists */}
                {googleDriveUrl && googleDriveUrl.toLowerCase().includes('google') && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      ref={heroLegislatorButtonRef}
                      variant="contained"
                      size="large"
                      startIcon={<DescriptionIcon />}
                      onClick={() => {
                        trackLegislatorPackClick(billConfig?.pageTitle || billSlug || 'Unknown', state);
                        window.open(googleDriveUrl, '_blank', 'noopener,noreferrer');
                      }}
                      sx={{
                        minWidth: 250,
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        py: 2,
                        px: 5,
                        boxShadow: 4,
                        bgcolor: 'primary.main',
                        '&:hover': { boxShadow: 8, transform: 'translateY(-3px)', bgcolor: 'primary.dark' },
                        transition: 'all 0.3s ease-in-out',
                      }}
                    >
                      View Legislator Pack
                    </Button>
                  </Box>
                )}
                {/* View Bill button - shown only when a real external state legislature URL exists */}
                {billUrl && billUrl.startsWith('http') && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<DescriptionIcon />}
                    onClick={() => {
                      trackOfficialBillLinkClick(billConfig?.pageTitle || billSlug || 'Unknown', state);
                      window.open(billUrl, '_blank', 'noopener,noreferrer');
                    }}
                    sx={{
                      minWidth: 250,
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      py: 2,
                      px: 5,
                      boxShadow: 4,
                      bgcolor: 'primary.main',
                      '&:hover': { boxShadow: 8, transform: 'translateY(-3px)', bgcolor: 'primary.dark' },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    View Bill
                  </Button>
                )}
                {/* No URL fallback - only show when neither Legislator Pack nor a real external bill URL exists */}
                {billUrlError && (
                  <Alert severity="warning" sx={{ maxWidth: 500, textAlign: 'left' }}>
                    Legislator pack information could not be loaded. Please refresh the page to try again.
                  </Alert>
                )}
                {!billUrlError &&
                  billUrlInfo &&
                  !googleDriveUrl &&
                  !(billUrl && billUrl.startsWith('http')) &&
                  billUrlInfo?.billStatus !== 'Passed' && (
                    <Alert severity="info" sx={{ maxWidth: 500, textAlign: 'left' }}>
                      A legislator pack for this location does not exist yet.{' '}
                      <Link to="/join-the-team" style={{ color: 'inherit', fontWeight: 600 }}>
                        Join the Movement
                      </Link>{' '}
                      to advocate for this bill in your area.
                    </Alert>
                  )}
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DescriptionIcon />}
                  onClick={() => {
                    const directory = document.getElementById('bill-state-directory');
                    if (directory) {
                      directory.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  sx={{
                    minWidth: 250,
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    py: 2,
                    px: 5,
                    boxShadow: 4,
                    bgcolor: 'primary.main',
                    '&:hover': { boxShadow: 8, transform: 'translateY(-3px)', bgcolor: 'primary.dark' },
                    transition: 'all 0.3s ease-in-out',
                  }}
                  aria-label="Scroll to state bill directory"
                >
                  Explore Bills
                </Button>
              </Box>
            )}
            {/* Share button should appear in both state-specific and directory modes */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ShareButton
                billTitle={billConfig.pageTitle}
                stateName={getCurrentStateName()}
                size="large"
                variant="text"
              />
              {googleDriveExportPdfUrl && (
                <Button
                  component="a"
                  href={googleDriveExportPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="large"
                  variant="text"
                  startIcon={<DownloadIcon />}
                >
                  Download
                </Button>
              )}
            </Box>
          </Box>
        </Container>
        {/* Hero image – desktop only, positioned flush to the right window edge, full height of wrapper */}
        <Box
          component="img"
          src={heroImageUrl}
          alt=""
          aria-hidden="true"
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            right: 0,
            top: 0,
            height: '100%',
            width: 'auto',
            opacity: 0.3,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      </Box>
      <WhyBillMatters whyItMatters={billConfig.whyItMatters} country={country} />
      {!isStateMode && (
        <Container id="bill-state-directory">
          <BillStateDirectory billSlug={billSlug!} billTitle={billConfig.pageTitle} country={country} />
        </Container>
      )}
      {/* DEPRECATED: Bill text section removed — see docs/BILL_TEXT_FEATURE.md */}

      {/* Location Prompt Dialog */}
      <BillLocationPromptDialog
        open={showLocationPrompt}
        onClose={handleDismissPrompt}
        billConfig={billConfig}
        country={country}
      />

      {/* Floating Legislator Pack button for mobile */}
      {isStateMode &&
        observerInitialized &&
        showFloatingButton &&
        googleDriveUrl &&
        googleDriveUrl.toLowerCase().includes('google') && (
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              zIndex: 1000,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<DescriptionIcon />}
                onClick={() => {
                  trackLegislatorPackClick(billConfig?.pageTitle || billSlug || 'Unknown', state);
                  window.open(googleDriveUrl, '_blank', 'noopener,noreferrer');
                }}
                sx={{
                  flex: 1,
                  fontSize: '1rem',
                  fontWeight: 700,
                  py: 1.5,
                  whiteSpace: 'nowrap',
                  borderRadius: '9999px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  bgcolor: 'primary.main',
                  '&:hover': { boxShadow: '0 6px 25px rgba(0, 0, 0, 0.4)', bgcolor: 'primary.dark' },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                View Legislator Pack
              </Button>
              {googleDriveExportPdfUrl && (
                <Tooltip title="Download Legislator Pack">
                  <IconButton
                    component="a"
                    href={googleDriveExportPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="large"
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      borderRadius: '9999px',
                      px: 2,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                      '&:hover': { bgcolor: 'primary.dark', boxShadow: '0 6px 25px rgba(0, 0, 0, 0.4)' },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    <DownloadIcon sx={{ color: 'black' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}
    </>
  );
};

export default BillDetailPage;
